import { RootState } from "@base/store";
import { WithAnalytics } from "@base/utils/hoc/withSendPageAnalytics";
import Button from "@components/atoms/button";
import TextField from "@components/atoms/input";
import { CalendarDay } from "@components/molecules/calendar-day";
import GameInfo from "@components/molecules/game-Info-card";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import theme from "@config/theme";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDraggable } from "react-use-draggable-scroll";

import { gameDetailsActions } from "../game-details/gameDetails.slice";
import { gameActions } from "../game/game.slice";
import { GameProps } from "../game/game.type";
import {
  CalendarContainer,
  Content,
  ContentHeader,
  GamesContainer,
  NoGamesTypography,
  NoGamesWrapper,
  Wrapper,
} from "./marketplace.styled";
import { SelectedDateProps } from "./marketplace.type";

import { ReactComponent as CreateGameIcon } from "@assets/images/player/Sport, Outdoor _ soccer, football, game, activity, man, running, play.svg";

type ReduxProps = ConnectedProps<typeof connector>;

const Marketplace = ({
  setSelectedGame,
  getGameById,
  marketplaceGames,
}: ReduxProps) => {
  const navigate = useNavigate();

  const contentTitleRef = useRef<HTMLDivElement>();
  const ref =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const { events } = useDraggable(ref); // Now we pass the reference to the useDraggable hook:

  const selectedGame = useSelector((state: RootState) => state.selectedGame);
  const user = useSelector((state: RootState) => state.user.user);
  const getNext30DaysInUTC = () => {
    const today = new Date();
    const next30Days = [];

    for (let i = 0; i < 31; i++) {
      const currentDate = new Date(today);
      currentDate.setUTCDate(today.getUTCDate() + i);
      next30Days.push(currentDate.toISOString());
    }

    return next30Days;
  };
  const [selectedDate, setSelectedDate] = useState<null | SelectedDateProps>({
    date: getNext30DaysInUTC()[0],
    index: 0,
  });
  const [pastGameInfo, setPastGameInfo] = useState<GameProps[]>([]);

  const handleGameSelection = async (gameID: string) => {
    const res = await getGameById({ id: gameID, isAuth: true });
    if (res?.meta?.requestStatus === "fulfilled") {
      setSelectedGame(res?.payload);
    }
  };

  const getMonthFromDate = (dateString: string) => {
    const date = new Date(dateString);
    const monthAbbreviation = new Intl.DateTimeFormat("en-US", {
      month: "short",
    }).format(date);
    return monthAbbreviation.toUpperCase();
  };

  const getDayFromDate = (dateString: string) => {
    const date = new Date(dateString);
    const dayOfMonth = date.getUTCDate();
    return dayOfMonth;
  };

  const getDayNameFromDate = (dateString: string) => {
    const date = new Date(dateString);
    const dayAbbreviation = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
    }).format(date);
    return dayAbbreviation.toUpperCase();
  };

  const filterGames = (gamesArray: GameProps[]) => {
    const games: GameProps[] = [];
    gamesArray.forEach((game: GameProps) => {
      games.push(game);
    });
    setPastGameInfo(games);
  };

  const getGamesInfo = async () => {
    const res = await marketplaceGames({
      address: formik.values.address,
      postCode: formik.values.postCode,
      date: selectedDate?.date!,
    });
    if (res?.meta?.requestStatus === "fulfilled") {
      filterGames(res?.payload);
    }
  };

  const handleBack = debounce(() => {
    navigate("/home");
  }, 300); // This is to prevent double clicks

  const formik = useFormik({
    initialValues: {
      postCode: "",
      address: "",
    },
    onSubmit: () => {},
  });

  const marketplaceRender = () => (
    <Layout
      isLayout
      HeaderChildren={<Header title="Find A Game" handleBack={handleBack} />}
      CardChildren={<></>}
    >
      <Content>
        <ContentHeader ref={contentTitleRef}>
          <Box mt={4}>
            <Wrapper>
              <CalendarContainer {...events} ref={ref}>
                {getNext30DaysInUTC().map((date, index) => {
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        if (selectedDate?.index === index) {
                          setSelectedDate(null);
                        } else {
                          setSelectedDate({
                            index,
                            date,
                          });
                        }
                      }}
                    >
                      <CalendarDay
                        isSelected={index === selectedDate?.index}
                        month={getMonthFromDate(date)}
                        day={getDayFromDate(date)}
                        name={getDayNameFromDate(date)}
                      />
                    </div>
                  );
                })}
              </CalendarContainer>
            </Wrapper>
            <Box
              sx={{
                display: "flex",
                gap: "5px",
              }}
            >
              <TextField
                placeholder={"Address"}
                name="address"
                fullWidth
                label="Address"
                size="small"
                height="40px"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={formik.errors.address}
                touched={formik.touched.address}
              />
              {/*<TextField
                placeholder={"Post Code"}
                name="postCode"
                fullWidth
                size="small"
                height="40px"
                label="Post Code"
                value={formik.values.postCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorMessage={formik.errors.postCode}
                touched={formik.touched.postCode}
              />*/}
            </Box>
          </Box>
          <GamesContainer>
            {pastGameInfo && pastGameInfo?.length > 0 ? (
              pastGameInfo?.map((game, index) => (
                <Box key={index}>
                  <GameInfo
                    key={index}
                    rsvp="A"
                    teamA={game?.sides[0]?.side_name}
                    teamB={game?.sides[1]?.side_name}
                    scoreA={game?.sides[0]?.score?.score_value}
                    scoreB={game?.sides[1]?.score?.score_value}
                    startDate={game?.end_date}
                    location={game?.address?.properties?.name || game?.location}
                    title={game?.title}
                    onClick={() => handleGameSelection(game?.id)}
                  />
                </Box>
              ))
            ) : (
              <NoGamesWrapper>
                <NoGamesTypography>
                  {"No games today. Submit a game and find other players."}
                </NoGamesTypography>
                <CreateGameIcon />
              </NoGamesWrapper>
            )}
          </GamesContainer>
        </ContentHeader>
        <Button
          iscta
          textColor={theme.palette.grey[100]}
          text="Submit your game"
          type="button"
          color="primary"
          onClick={() => {
            navigate("/game", {
              state: { from: "/marketplace" },
            });
          }}
          startIcon={<AddBoxIcon />}
        />
      </Content>
    </Layout>
  );

  useEffect(() => {
    getGamesInfo();
  }, [formik.values.address, formik.values.postCode, selectedDate?.date]);

  useEffect(() => {
    if (selectedGame.id) {
      navigate("/game-details", {
        state: { from: "/marketplace" },
      });
    }
  }, [selectedGame]);

  return <>{marketplaceRender()}</>;
};

const mapDispatchToProps = {
  setCookieConsent: LoaderSnackbarWrapperActions.setGlobalAppearances,
  getGameById: gameDetailsActions.getGameById,
  reset: gameDetailsActions.reset,
  setSelectedGame: gameDetailsActions.setSelectedGame,
  marketplaceGames: gameActions.marketplaceGames,
};

const connector = connect(undefined, mapDispatchToProps);
const MarketplaceRedux = connector(WithAnalytics(Marketplace));

export { MarketplaceRedux as Marketplace };
